import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DISPUTE_ID_STORAGE_KEY,
  ORDER_ID_STORAGE_KEY,
  PAYOUT_ID_STORAGE_KEY,
  STATEMENT_ID_STORAGE_KEY,
} from './catalog.js';
import type { PayoutPricing } from './financeApi.js';

/** 07-02 ○ poll interval for finance overview, payouts, and deductions. */
export const FINANCE_POLL_MS = 30_000;

export function useOrderIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('orderId') ?? params.get('id') ?? getStoredOrderId();
}

export function useStatementIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('statementId') ?? params.get('id') ?? getStoredStatementId();
}

export function usePayoutIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('payoutId') ?? params.get('id') ?? getStoredPayoutId();
}

export function useDisputeIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('disputeId') ?? params.get('id') ?? getStoredDisputeId();
}

/** Table from 64rem, cards below — only one list in the DOM so amounts are not duplicated. */
export function useWidePayoutHistory(): boolean {
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 64rem)').matches : true,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 64rem)');
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return wide;
}

export function getStoredOrderId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ORDER_ID_STORAGE_KEY);
}

export function setStoredOrderId(orderId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ORDER_ID_STORAGE_KEY, orderId);
}

export function getStoredStatementId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(STATEMENT_ID_STORAGE_KEY);
}

export function setStoredStatementId(statementId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STATEMENT_ID_STORAGE_KEY, statementId);
}

export function getStoredPayoutId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(PAYOUT_ID_STORAGE_KEY);
}

export function setStoredPayoutId(payoutId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PAYOUT_ID_STORAGE_KEY, payoutId);
}

export function getStoredDisputeId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(DISPUTE_ID_STORAGE_KEY);
}

export function setStoredDisputeId(disputeId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(DISPUTE_ID_STORAGE_KEY, disputeId);
}

export function financeOverviewPath(): string {
  return '/finance-overview';
}

export function financeBeatPayPath(): string {
  return '/finance/beat-pay';
}

export function financeTransactionsPath(): string {
  return '/finance/transactions';
}

export function financeReportsPath(): string {
  return '/finance/reports';
}

export function orderEarningsPath(orderId?: string): string {
  return orderId ? `/order-earnings?orderId=${encodeURIComponent(orderId)}` : '/order-earnings';
}

export function statementsPath(): string {
  return '/statements';
}

export function statementDetailPath(statementId: string): string {
  return `/statement-detail?statementId=${encodeURIComponent(statementId)}`;
}

export function payoutsPath(payoutId?: string): string {
  return payoutId ? `/payouts?payoutId=${encodeURIComponent(payoutId)}` : '/payouts';
}

export function instantPayoutPath(): string {
  return '/instant-payout';
}

export function nextDayPayoutPath(): string {
  return '/instant-payout?method=next-day';
}

export function deductionsAndDisputesPath(disputeId?: string): string {
  return disputeId
    ? `/deductions-and-disputes?disputeId=${encodeURIComponent(disputeId)}`
    : '/deductions-and-disputes';
}

export function financeInvoicesPath(): string {
  return '/finance-invoices';
}

export type PricedPayoutMethod = 'instant' | 'next-day';

const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export function formatPolicyMoney(pricing: PayoutPricing, amountMinor: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: pricing.currency,
  }).format(amountMinor / 100);
}

export function calculatePayoutFeeMinor(
  pricing: PayoutPricing,
  method: PricedPayoutMethod,
  amountMinor: number,
): number {
  if (method === 'next-day') return pricing.nextDay.feeMinor;
  if (amountMinor < pricing.instant.flatFeeUpperExclusiveMinor) {
    return pricing.instant.flatFeeMinor;
  }
  return Math.round((amountMinor * pricing.instant.rateBps) / 10_000);
}

export function payoutMinimumMinor(pricing: PayoutPricing, method: PricedPayoutMethod): number {
  return method === 'instant' ? pricing.instant.minimumMinor : pricing.nextDay.minimumMinor;
}

export function payoutMaximumMinor(
  pricing: PayoutPricing,
  method: PricedPayoutMethod,
): number | null {
  return method === 'instant' ? pricing.instant.maximumMinor : null;
}

export function formatPolicyDateTime(value: string, timeZone: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unavailable';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(parsed);
}

export function formatPolicyHour(hourLocal: number): string {
  return `${String(hourLocal).padStart(2, '0')}:00`;
}

export function formatPolicyCutoff(cutoffMinutes: number): string {
  const hour = Math.floor(cutoffMinutes / 60);
  const minute = cutoffMinutes % 60;
  if (hour === 12 && minute === 0) return 'noon';
  if (hour === 0 && minute === 0) return 'midnight';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function formatPolicyTimeZone(timeZone: string): string {
  return timeZone === 'Europe/London' ? 'London time' : timeZone;
}

export function formatInstantPricingCopy(pricing: PayoutPricing): string {
  const flatUpper = pricing.instant.flatFeeUpperExclusiveMinor - 1;
  const percent = `${(pricing.instant.rateBps / 100).toFixed(2).replace(/\.?0+$/, '')}%`;
  return `${formatPolicyMoney(pricing, pricing.instant.flatFeeMinor)} for ${formatPolicyMoney(pricing, pricing.instant.minimumMinor)}–${formatPolicyMoney(pricing, flatUpper)}; ${percent}, rounded to the nearest penny, from ${formatPolicyMoney(pricing, pricing.instant.flatFeeUpperExclusiveMinor)}. Maximum ${formatPolicyMoney(pricing, pricing.instant.maximumMinor)}.`;
}

export function formatNextDayPricingCopy(pricing: PayoutPricing): string {
  return `${formatPolicyMoney(pricing, pricing.nextDay.feeMinor)} fee. Minimum ${formatPolicyMoney(pricing, pricing.nextDay.minimumMinor)}. Request by ${formatPolicyCutoff(pricing.nextDay.cutoffMinutes)} ${formatPolicyTimeZone(pricing.timeZone)}. Estimated arrival ${formatPolicyDateTime(pricing.nextDay.estimatedArrival, pricing.timeZone)}.`;
}

export function formatScheduledPricingCopy(pricing: PayoutPricing): string {
  const weekday = WEEKDAY_LABELS[pricing.scheduled.weekday] ?? 'Scheduled day';
  return `${weekday} at ${formatPolicyHour(pricing.scheduled.hourLocal)} ${formatPolicyTimeZone(pricing.timeZone)}. ${formatPolicyMoney(pricing, pricing.scheduled.feeMinor)} fee. If the balance does not cover the fee, it carries forward to the next scheduled payout.`;
}

export function formatNextScheduledPayout(pricing: PayoutPricing): string {
  return formatPolicyDateTime(pricing.scheduled.nextProcessingAt, pricing.timeZone);
}

/** Never return more than four digits — full account numbers must not reach the UI. */
export function maskDestinationLast4(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 4) return digits.slice(-4);
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.slice(-4);
}

export function formatDestinationLast4(last4: string | null | undefined): string {
  const masked = maskDestinationLast4(last4);
  return masked ? `···· ${masked}` : '—';
}

/** 07-05 optional branch breakdown — only when the payout payload includes branchId. */
export function uniquePayoutBranchIds(payouts: Array<{ branchId?: string | null }>): string[] {
  const ids = new Set<string>();
  for (const payout of payouts) {
    if (payout.branchId) ids.add(payout.branchId);
  }
  return [...ids].sort();
}

export function computePayoutPreview(input: {
  availableMinor: number;
  amountMinor: number;
  pricing?: PayoutPricing;
  method: PricedPayoutMethod;
}): {
  feeMinor: number;
  netReceivedMinor: number;
  resultingBalanceMinor: number;
  canSubmit: boolean;
  blockedReason: string | null;
} {
  const amountMinor = Number.isFinite(input.amountMinor) ? Math.trunc(input.amountMinor) : 0;
  const availableMinor = Number.isFinite(input.availableMinor)
    ? Math.trunc(input.availableMinor)
    : 0;
  const feeMinor = input.pricing
    ? calculatePayoutFeeMinor(input.pricing, input.method, amountMinor)
    : 0;
  const netReceivedMinor = amountMinor - feeMinor;
  const resultingBalanceMinor = availableMinor - amountMinor;
  if (!input.pricing) {
    return {
      feeMinor,
      netReceivedMinor,
      resultingBalanceMinor,
      canSubmit: false,
      blockedReason: 'Payout pricing is unavailable. Refresh before requesting a payout.',
    };
  }
  const minimumMinor = payoutMinimumMinor(input.pricing, input.method);
  const maximumMinor = payoutMaximumMinor(input.pricing, input.method);
  const methodLabel =
    input.method === 'instant'
      ? 'instant bank payout from your available balance'
      : 'next-day payout';
  if (availableMinor <= 0) {
    return {
      feeMinor,
      netReceivedMinor: 0,
      resultingBalanceMinor: availableMinor,
      canSubmit: false,
      blockedReason: `Nothing is available for a ${methodLabel}.`,
    };
  }
  if (amountMinor <= 0) {
    return {
      feeMinor,
      netReceivedMinor,
      resultingBalanceMinor,
      canSubmit: false,
      blockedReason: 'Enter an amount to pay out.',
    };
  }
  if (amountMinor > availableMinor) {
    return {
      feeMinor,
      netReceivedMinor,
      resultingBalanceMinor,
      canSubmit: false,
      blockedReason: 'Amount is more than the available balance.',
    };
  }
  if (availableMinor < minimumMinor || amountMinor < minimumMinor) {
    return {
      feeMinor,
      netReceivedMinor,
      resultingBalanceMinor,
      canSubmit: false,
      blockedReason: `You need at least ${formatPolicyMoney(input.pricing, minimumMinor)} available.`,
    };
  }
  if (maximumMinor != null && amountMinor > maximumMinor) {
    return {
      feeMinor,
      netReceivedMinor,
      resultingBalanceMinor,
      canSubmit: false,
      blockedReason: `The maximum instant bank payout is ${formatPolicyMoney(input.pricing, maximumMinor)}.`,
    };
  }
  if (amountMinor <= feeMinor) {
    return {
      feeMinor,
      netReceivedMinor,
      resultingBalanceMinor,
      canSubmit: false,
      blockedReason: `Amount must be greater than the ${formatPolicyMoney(input.pricing, feeMinor)} fee.`,
    };
  }
  return {
    feeMinor,
    netReceivedMinor,
    resultingBalanceMinor,
    canSubmit: true,
    blockedReason: null,
  };
}

export const COMPLETE_PAYOUT_SETUP_COPY = 'Complete payout setup in BeatQ Pay';

const FINANCE_STATUS_LABELS: Record<string, string> = {
  PAID: 'Paid',
  PENDING: 'Pending',
  RESERVED: 'Sending',
  PROCESSING: 'On the way',
  SENT: 'Sent to bank',
  FAILED: 'Failed',
  RETURNED: 'Returned',
  RECONCILIATION_REQUIRED: 'Needs review',
  INSTANT: 'Instant',
  NEXT_DAY: 'Next day',
  SCHEDULED_WEEKLY: 'Weekly',
  FINALISED: 'Finalised',
  FINALIZED: 'Finalised',
  DRAFT: 'Draft',
  OPEN: 'Open',
  OVERDUE: 'Overdue',
  ISSUED: 'Issued',
  EVIDENCE_SUBMITTED: 'Evidence submitted',
  NOT_STARTED: 'Not set up',
  PENDING_ONBOARDING: 'Setup in progress',
  PENDING_VERIFICATION: 'Being reviewed',
  ACTIVE: 'Active',
  MISSING: 'No bank account',
  VALID: 'Verified',
  INVALID: 'Needs attention',
  DEDUCTION: 'Deduction',
  QUALITY: 'Quality',
  ITEM_MISSING: 'Item missing',
  CUSTOMER_CLAIM: 'Customer claim',
  ACCEPTED: 'Accepted',
  WON: 'Won',
  LOST: 'Lost',
};

export function formatFinanceStatus(status: string | null | undefined): string {
  if (!status?.trim()) return 'Unknown';
  const key = status.trim().toUpperCase().replace(/\s+/g, '_');
  if (FINANCE_STATUS_LABELS[key]) return FINANCE_STATUS_LABELS[key];
  return status
    .trim()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatPayoutLifecycle(input: {
  state?: string | null;
  providerTransferStatus?: string | null;
  providerTrackingStatus?: string | null;
}): string {
  const state = input.state?.trim().toUpperCase();
  const provider = input.providerTransferStatus?.trim().toLowerCase();
  const tracking = input.providerTrackingStatus?.trim().toLowerCase();
  if (state === 'RETURNED' || provider === 'returned') return 'Returned';
  if (state === 'FAILED' || ['failed', 'refused', 'error', 'cancelled'].includes(provider ?? '')) {
    return 'Failed';
  }
  if (state === 'RECONCILIATION_REQUIRED') return 'Needs review';
  if (state === 'SENT') return 'Sent to bank';
  if (tracking === 'credited' || provider === 'credited' || state === 'PAID') return 'Delivered';
  if (provider === 'booked' || state === 'PROCESSING') return 'Sent';
  if (provider === 'authorised' || state === 'RESERVED') return 'Processing';
  return 'Initiated';
}

export function isBeatPayPayoutReady(status: string | null | undefined): boolean {
  return status === 'ACTIVE' || status === 'VALID';
}

export function resolvePayoutRequestBlock(input: {
  method: PricedPayoutMethod;
  pricing?: PayoutPricing;
  availableMinor: number;
  canRequest?: boolean;
  capabilityReason?: string | null;
  beatPayReady: boolean;
  nextPayoutLabel: string;
}): string | null {
  if (!input.beatPayReady) {
    return input.capabilityReason?.trim() || COMPLETE_PAYOUT_SETUP_COPY;
  }
  if (input.canRequest === false && input.capabilityReason) {
    return input.capabilityReason;
  }
  if (!input.pricing) {
    return 'Payout pricing is unavailable. Refresh before requesting a payout.';
  }
  const minimumMinor = payoutMinimumMinor(input.pricing, input.method);
  if (input.availableMinor <= 0) {
    return `Nothing is eligible for an instant bank payout. The next processing window is ${input.nextPayoutLabel}.`;
  }
  if (input.availableMinor < minimumMinor) {
    return `You need at least ${formatPolicyMoney(input.pricing, minimumMinor)} eligible. The next processing window is ${input.nextPayoutLabel}.`;
  }
  return null;
}

export function downloadTextFile(
  filename: string,
  contents: string,
  mime = 'text/csv;charset=utf-8',
): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
